// Pure helpers for treating ExerciseTemplateElement[] (static),
// ExerciseTemplateInteractiveElement[] (interactive) and
// ExerciseTemplateContainer[] (layout regions) as one merged, top-to-bottom
// block list - there is no unified ordering on the backend (three tables,
// three independently-numbered `position` columns), so this merge is a
// purely frontend construct.
//
// `containerId` scopes the merge: null (default) is the exercise's TOP
// level - every static/interactive block with no container, plus every
// container itself. A given container's id scopes it to just that
// container's own children (static/interactive blocks whose `container`
// matches) - containers don't nest, so no container entries come back in
// that case. Both BlockList.jsx (top-level, containerId omitted) and
// ContainerCard.jsx (containerId = that container's own id) call this same
// function, as does courses/preview/ExercisePreviewList.jsx for read-only
// rendering - one implementation, no drift between editing and preview.

export const BLOCK_KIND_STATIC = "static";
export const BLOCK_KIND_INTERACTIVE = "interactive";
export const BLOCK_KIND_CONTAINER = "container";

export function containerIdOf(block) {
    if (!block.container) return null;
    return typeof block.container === "object" ? block.container.id : block.container;
}

export function mergeBlocks(elements, interactiveElements, containers, containerId = null) {
    const staticEntries = (elements || [])
        .filter((block) => containerIdOf(block) === containerId)
        .map((block) => ({ kind: BLOCK_KIND_STATIC, block }));
    const interactiveEntries = (interactiveElements || [])
        .filter((block) => containerIdOf(block) === containerId)
        .map((block) => ({ kind: BLOCK_KIND_INTERACTIVE, block }));
    const containerEntries = containerId === null
        ? (containers || []).map((block) => ({ kind: BLOCK_KIND_CONTAINER, block }))
        : [];
    return [...staticEntries, ...interactiveEntries, ...containerEntries].sort((a, b) => {
        const posDiff = (a.block.position || 0) - (b.block.position || 0);
        if (posDiff !== 0) return posDiff;
        // Tiebreak by id so entries sharing a position (e.g. freshly created
        // blocks) render in a stable, creation order instead of jumping
        // around between re-renders.
        return (a.block.id || 0) - (b.block.id || 0);
    });
}

// Position for a brand-new block appended at the end of the merged list.
export function nextPosition(mergedList) {
    if (!mergedList || mergedList.length === 0) return 0;
    return Math.max(...mergedList.map((entry) => entry.block.position || 0)) + 1;
}

// Swapping two entries' `position` values inverts their relative order
// regardless of the absolute values involved (works even with gaps) -
// returns the two entries with positions already swapped, ready to PUT,
// or null if the move would go past either edge of the list.
export function computeMove(mergedList, index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= mergedList.length) return null;

    const current = mergedList[index];
    const target = mergedList[targetIndex];
    return [
        { ...current, block: { ...current.block, position: target.block.position } },
        { ...target, block: { ...target.block, position: current.block.position } },
    ];
}
